<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export type ChipFilter = { kind: string; value: string }

export type ChipGroup = {
    kind: string
    label: string
    options: string[]
    uppercase?: boolean
}

/**
 * The one filter control on the catalog detail page.
 *
 * Each group is a separate question — a currency and a country do not imply each
 * other, since UK vendors quote resell in GBP and trade-in in EUR — but only one
 * selection is live at a time: stacking them narrows to mostly empty tables, and
 * the leading "All" is the single way back out of whichever is set.
 *
 * Chips rather than a dropdown, because the set an account tracks is small and worth
 * seeing without opening anything.
 *
 * **A group with one option still renders, stated rather than offered.** Hiding it
 * was worse than useless: a chart of Swiss listings showed its currency and nothing
 * else, so the reader could not tell "these are all CH" from "country was never
 * considered here". One value is not a choice, but it is very much an answer.
 */
const props = defineProps<{
    groups: ChipGroup[]
    modelValue: ChipFilter | null
}>()

const emit = defineEmits<{
    (event: 'update:modelValue', value: ChipFilter | null): void
}>()

const { t } = useI18n()

/** Declared order is kept, so the row reads the same left to right on every card. */
const visible = computed(() => props.groups.filter((group) => group.options.length > 0))

/** One option is the scope: true by definition, and nothing to pick. */
const isStated = (group: ChipGroup) => group.options.length === 1

/** Whether anything on the row can actually be cleared. */
const hasSelectable = computed(() => visible.value.some((group) => !isStated(group)))

const isActive = (group: ChipGroup, option: string) =>
    isStated(group) || (props.modelValue?.kind === group.kind && props.modelValue.value === option)

const select = (group: ChipGroup, option: string) => {
    if (isStated(group)) {
        return
    }

    emit('update:modelValue', { kind: group.kind, value: option })
}
</script>

<template>
    <div v-if="visible.length > 0" class="flex flex-wrap items-center gap-x-4 gap-y-2">
        <!-- Leads the row wherever it appears, as on every other card. Rendered only
             where something can be cleared: with every group settled, "All" would be a
             button that does nothing. -->
        <button
            v-if="hasSelectable"
            type="button"
            class="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors"
            :class="
                modelValue === null
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
            "
            @click="emit('update:modelValue', null)"
        >
            {{ t('catalog.details.competitorPrices.allFilters') }}
        </button>

        <div v-for="group in visible" :key="group.kind" class="flex items-center gap-2">
            <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {{ group.label }}
            </span>
            <div class="inline-flex flex-wrap rounded-md border p-0.5">
                <!--
                    A stated group's chip is a span, not a button: it is already selected
                    and there is nothing else to pick, so a button would promise a choice
                    the section cannot offer. Everything else about it is the same, which
                    is what keeps the row reading as one control.
                -->
                <component
                    :is="isStated(group) ? 'span' : 'button'"
                    v-for="option in group.options"
                    :key="option"
                    :type="isStated(group) ? undefined : 'button'"
                    class="rounded px-2.5 py-1 text-xs font-medium transition-colors"
                    :class="[
                        group.uppercase ? 'uppercase' : 'tabular-nums',
                        isStated(group) ? 'cursor-default' : '',
                        isActive(group, option)
                            ? 'bg-secondary text-secondary-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                    ]"
                    @click="select(group, option)"
                >
                    {{ option }}
                </component>
            </div>
        </div>
    </div>
</template>
